import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { AlignLeftIcon, CheckSquareIcon, Heading1Icon, Heading2Icon, ImagePlusIcon, LinkIcon, ListIcon, MessageSquareTextIcon, SeparatorHorizontalIcon, Trash2Icon, XIcon } from 'lucide-react';
import { JournalBlock, JournalBlockType } from '../../lib/types';
import { cn } from '../../lib/cn';
const blockOptions: {
  type: JournalBlockType;
  label: string;
  icon: React.ElementType;
}[] = [{
  type: 'text',
  label: 'Texto',
  icon: AlignLeftIcon
}, {
  type: 'heading',
  label: 'Título',
  icon: Heading1Icon
}, {
  type: 'subheading',
  label: 'Subtítulo',
  icon: Heading2Icon
}, {
  type: 'list',
  label: 'Lista',
  icon: ListIcon
}, {
  type: 'checklist',
  label: 'Checklist',
  icon: CheckSquareIcon
}, {
  type: 'divider',
  label: 'Separador',
  icon: SeparatorHorizontalIcon
}, {
  type: 'comment',
  label: 'Comentario',
  icon: MessageSquareTextIcon
}, {
  type: 'link',
  label: 'Enlace',
  icon: LinkIcon
}];
function readImage(file: File): Promise<{
  url: string;
  name: string;
}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      url: String(reader.result),
      name: file.name
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
function placeholder(type: JournalBlockType): string {
  const labels: Record<Exclude<JournalBlockType, 'divider' | 'image'>, string> = {
    text: 'Escribe algo…',
    heading: 'Título',
    subheading: 'Subtítulo',
    list: 'Elemento de lista',
    checklist: 'Tarea o confirmación',
    comment: 'Comentario o reflexión…',
    link: 'Pega un enlace…'
  };
  return type === 'divider' || type === 'image' ? '' : labels[type];
}
type BlockDraft = Pick<JournalBlock, 'type' | 'content' | 'caption' | 'checked' | 'width'>;
interface Props {
  blocks: JournalBlock[];
  onInsert: (drafts: BlockDraft[], afterId?: string | null) => void;
  onUpdate: (id: string, patch: Partial<JournalBlock>) => void;
  onDelete: (id: string) => void;
  onReorder: (ids: string[]) => void;
  onDirty: () => void;
}
export function JournalBlockEditor({
  blocks,
  onInsert,
  onUpdate,
  onDelete,
  onReorder,
  onDirty
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [fullImage, setFullImage] = useState<JournalBlock | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);

  // Focus the block that was just created, so typing continues uninterrupted.
  useEffect(() => {
    if (!focusId) return;
    const node = canvasRef.current?.querySelector<HTMLElement>(`[data-block-id="${focusId}"] [data-block-input]`);
    node?.focus();
    setFocusId(null);
  }, [focusId, blocks]);
  const insertBlock = (type: JournalBlockType, afterId?: string | null) => {
    onInsert([{
      type,
      content: '',
      caption: '',
      checked: false,
      width: type === 'image' ? 90 : undefined
    }], afterId ?? activeBlockId);
  };
  const insertImages = useCallback(async (files: FileList | File[]) => {
    const images = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (!images.length) return;
    const decoded = await Promise.all(images.map(readImage));
    onInsert(decoded.map(image => ({
      type: 'image' as const,
      content: image.url,
      caption: image.name,
      width: 90 as const
    })), activeBlockId);
    onDirty();
  }, [activeBlockId, onDirty, onInsert]);
  const move = (id: string, targetIndex: number) => {
    const ids = blocks.map(block => block.id);
    const current = ids.indexOf(id);
    if (current < 0 || targetIndex < 0 || targetIndex >= ids.length || current === targetIndex) return;
    ids.splice(current, 1);
    ids.splice(targetIndex, 0, id);
    onReorder(ids);
    onDirty();
  };
  const captureActiveBlock = (event: React.FocusEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const row = target.closest<HTMLElement>('[data-block-id]');
    if (row?.dataset.blockId) setActiveBlockId(row.dataset.blockId);
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.items).map(item => item.getAsFile()).filter((file): file is File => !!file && file.type.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    void insertImages(files);
  };

  // Clicking anywhere on the blank sheet starts (or continues) writing.
  const startWriting = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-block-id], button, input, textarea, a')) return;
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'text' && !last.content) {
      const node = canvasRef.current?.querySelector<HTMLElement>(`[data-block-id="${last.id}"] [data-block-input]`);
      node?.focus();
      return;
    }
    insertBlock('text', last?.id ?? null);
    onDirty();
  };
  return <section className="mt-7 border-t border-line pt-6" data-id="element-283" data-mp="1wymys6oiakry">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2" data-id="element-284" data-mp="1s4sild840nxm">
        <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-285" data-mp="1n67gt9d7lw72">Notas</h2>
        <div className="flex flex-wrap gap-0.5" data-id="element-286" data-mp="1n4e6nk3e0tub">
          {blockOptions.map(({
          type,
          label,
          icon: Icon
        }) => <button key={type} type="button" onClick={() => {
          insertBlock(type);
          onDirty();
        }} title={label} aria-label={label} className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] text-ink-soft transition-colors duration-150 hover:bg-surface hover:text-ink" data-id="element-287" data-mp="2fv622fcmpegl">
              <Icon className="h-3.5 w-3.5" data-id="element-288" data-mp="21xzjakrwnfrd" />
              <span className="hidden lg:inline" data-id="element-289" data-mp="uj4l0ozx3zki">{label}</span>
            </button>)}
          <button type="button" onClick={() => fileRef.current?.click()} title="Imagen" aria-label="Imagen" className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] text-ink-soft transition-colors duration-150 hover:bg-surface hover:text-ink" data-id="element-290" data-mp="qg873blbormw">
            <ImagePlusIcon className="h-3.5 w-3.5" data-id="element-291" data-mp="hz7mygqnnw4e" />
            <span className="hidden lg:inline" data-id="element-292" data-mp="my0yw4qanf2g">Imagen</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={event => {
          if (event.target.files) void insertImages(event.target.files);
          event.target.value = '';
        }} data-id="element-293" data-mp="975pmrbvuvyc" />
        </div>
      </div>

      <div ref={canvasRef} onFocusCapture={captureActiveBlock} onPaste={handlePaste} onClick={startWriting} onDragOver={event => {
      event.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={event => {
      event.preventDefault();
      setDragging(false);
      void insertImages(event.dataTransfer.files);
    }} className={cn('min-h-[520px] cursor-text bg-white pb-24 transition-colors duration-150', dragging && 'bg-accent-soft/20')} data-id="element-294" data-mp="482jf3k62jos">
        {blocks.length === 0 && <p className="select-none pt-1 text-[15px] leading-8 text-ink-soft/50" data-id="element-295" data-mp="3gpaoqloidp0l">
            Empieza a escribir…
            <br data-id="element-296" data-mp="3qpl0e1uwaodn" />
            <span className="text-[13px]" data-id="element-297" data-mp="3qp1igl58vzzm">o pega una captura con Ctrl + V</span>
          </p>}

        <div className="space-y-0.5" data-id="element-298" data-mp="3lrjhpi1dsr2p">
          {blocks.map((block, index) => <JournalBlockRow key={block.id} block={block} index={index} onFocus={() => setActiveBlockId(block.id)} onDropAt={fromId => move(fromId, index)} onUpdate={patch => {
          onUpdate(block.id, patch);
          onDirty();
        }} onDelete={() => {
          onDelete(block.id);
          onDirty();
        }} onEnter={() => {
          insertBlock('text', block.id);
          onDirty();
        }} onFocusNew={setFocusId} onOpenImage={() => setFullImage(block)} data-id="element-299" data-mp="3da9ay0782b3" data-mpi={index} />)}
        </div>
      </div>

      {fullImage && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/85 p-6" onClick={() => setFullImage(null)} data-id="element-300" data-mp="1oopkgdyaems8">
          <button className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20" aria-label="Cerrar imagen" data-id="element-301" data-mp="1tnk3nh062dcv">
            <XIcon className="h-5 w-5" data-id="element-302" data-mp="110g4vtro2v9j" />
          </button>
          <motion.img initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2
      }} src={fullImage.content} alt={fullImage.caption ?? 'Captura del journal'} className="max-h-full max-w-full rounded-xl object-contain shadow-pop" onClick={event => event.stopPropagation()} data-id="element-303" data-mp="15yhn6ch9qqjy" />
        </div>}
    </section>;
}
function JournalBlockRow({
  block,
  index,
  onFocus,
  onDropAt,
  onUpdate,
  onDelete,
  onEnter,
  onFocusNew,
  onOpenImage
}: {
  block: JournalBlock;
  index: number;
  onFocus: () => void;
  onDropAt: (fromId: string) => void;
  onUpdate: (patch: Partial<JournalBlock>) => void;
  onDelete: () => void;
  onEnter: () => void;
  onFocusNew: (id: string | null) => void;
  onOpenImage: () => void;
}) {
  const [over, setOver] = useState(false);
  const handleKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && block.type !== 'text' && block.type !== 'comment') {
      event.preventDefault();
      onEnter();
    }
  };
  const common = {
    value: block.content,
    onFocus,
    onKeyDown: handleKey,
    'data-block-input': true,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onUpdate({
      content: event.target.value
    }),
    placeholder: placeholder(block.type)
  };
  return <motion.article layout transition={{
    duration: 0.18
  }} data-block-id={block.id} draggable onDragStart={event => {
    const native = event as unknown as React.DragEvent;
    native.dataTransfer?.setData('text/journal-block', block.id);
  }} onDragOver={event => {
    const native = event as unknown as React.DragEvent;
    if (native.dataTransfer?.types.includes('text/journal-block')) {
      native.preventDefault();
      setOver(true);
    }
  }} onDragLeave={() => setOver(false)} onDrop={event => {
    const native = event as unknown as React.DragEvent;
    const fromId = native.dataTransfer?.getData('text/journal-block');
    if (fromId && fromId !== block.id) {
      native.preventDefault();
      onDropAt(fromId);
    }
    setOver(false);
  }} className={cn('group relative rounded-md px-1 py-0.5 transition-colors duration-150', over && 'ring-2 ring-accent/30')} data-id="element-304" data-mp="3ohl9fc3moasu">
      <button type="button" onClick={onDelete} className="absolute -left-6 top-1.5 hidden h-5 w-5 items-center justify-center rounded text-ink-soft/70 transition-colors duration-150 hover:bg-neg-soft hover:text-neg group-hover:flex" aria-label="Eliminar bloque" data-id="element-305" data-mp="xac6pztqveie">
        <Trash2Icon className="h-3 w-3" data-id="element-306" data-mp="sdc4y13xgxx3" />
      </button>

      {block.type === 'divider' && <hr className="my-3 border-line" data-id="element-307" data-mp="1g1ljqj2q4leq" />}
      {block.type === 'heading' && <input {...common} className="w-full bg-transparent px-1 py-1 text-2xl font-semibold tracking-tight placeholder:text-ink-soft/35 focus:outline-none" data-id="element-308" data-mp="1l0ydzovxcurc" />}
      {block.type === 'subheading' && <input {...common} className="w-full bg-transparent px-1 py-1 text-[16px] font-semibold placeholder:text-ink-soft/35 focus:outline-none" data-id="element-309" data-mp="16a7lsfqf7tnp" />}
      {block.type === 'text' && <textarea {...common} rows={1} onInput={event => {
      const el = event.currentTarget;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }} className="w-full resize-none overflow-hidden bg-transparent px-1 py-0.5 text-[15px] leading-8 placeholder:text-ink-soft/40 focus:outline-none" data-id="element-310" data-mp="1yympw780krk9" />}
      {block.type === 'list' && <div className="flex gap-2 px-1" data-id="element-311" data-mp="1zo6lofq0yhyk">
          <span className="pt-1.5 text-ink-soft" data-id="element-312" data-mp="1zndmq0xb4wqz">•</span>
          <input {...common} className="min-w-0 flex-1 bg-transparent py-1 text-[15px] leading-7 placeholder:text-ink-soft/40 focus:outline-none" data-id="element-313" data-mp="1up3nnf8gpc03" />
        </div>}
      {block.type === 'checklist' && <label className="flex cursor-text items-center gap-2 px-1" data-id="element-314" data-mp="1kxz92gmp7t5s">
          <input type="checkbox" checked={block.checked ?? false} onChange={event => onUpdate({
        checked: event.target.checked
      })} className="h-4 w-4 rounded border-line text-accent transition-colors duration-150 focus:ring-accent/30" data-id="element-315" data-mp="1gx5ljod64gdf" />
          <input {...common} className={cn('min-w-0 flex-1 bg-transparent py-1 text-[15px] leading-7 placeholder:text-ink-soft/40 focus:outline-none', block.checked && 'text-ink-soft line-through')} data-id="element-316" data-mp="3imbj2lj2hugu" />
        </label>}
      {block.type === 'comment' && <div className="flex gap-2 rounded-md border-l-2 border-line bg-surface/50 px-3 py-1.5" data-id="element-317" data-mp="3sl0upwkdeion">
          <MessageSquareTextIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-soft" data-id="element-318" data-mp="g6pnun2qzgr" />
          <textarea {...common} rows={2} className="w-full resize-none bg-transparent text-[14px] leading-7 placeholder:text-ink-soft/40 focus:outline-none" data-id="element-319" data-mp="2zx4uwizi8ann" />
        </div>}
      {block.type === 'link' && <div className="flex items-center gap-2 px-1" data-id="element-320" data-mp="38v8idku1vhi3">
          <LinkIcon className="h-4 w-4 text-ink-soft" data-id="element-321" data-mp="3dnrsu8xfxwio" />
          <input {...common} type="url" className="min-w-0 flex-1 bg-transparent py-1 text-[15px] text-accent underline decoration-accent/30 underline-offset-2 placeholder:text-ink-soft/40 placeholder:no-underline focus:outline-none" data-id="element-322" data-mp="3dpcs2bupa3mv" />
        </div>}
      {block.type === 'image' && <InlineImage block={block} onFocus={onFocus} onUpdate={onUpdate} onOpen={onOpenImage} data-id="element-323" data-mp="222lt7r8pejto" />}
    </motion.article>;
}
function InlineImage({
  block,
  onFocus,
  onUpdate,
  onOpen
}: {
  block: JournalBlock;
  onFocus: () => void;
  onUpdate: (patch: Partial<JournalBlock>) => void;
  onOpen: () => void;
}) {
  const width = block.width ?? 90;
  return <div className="px-1 py-1" onClick={onFocus} data-id="element-324" data-mp="2ksahx2lyp40l">
      <div style={{
      width: `${width}%`
    }} className="max-w-full" data-id="element-325" data-mp="2kuwrt8wbrvlz">
        <button type="button" onClick={onOpen} className="block w-full overflow-hidden rounded-lg border border-line bg-surface text-left transition-opacity duration-150 hover:opacity-95" data-id="element-326" data-mp="2fspcc9cadxew">
          <img src={block.content} alt={block.caption ?? 'Captura del journal'} className="max-h-[680px] w-full object-contain" data-id="element-327" data-mp="2prn37ix6q312" />
        </button>
        <div className="mt-1.5 flex flex-wrap items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100" data-id="element-328" data-mp="10f2ymapm648l">
          {[75, 80, 90, 100].map(size => <button key={size} type="button" onClick={() => onUpdate({
          width: size as 75 | 80 | 90 | 100
        })} className={cn('rounded-md px-1.5 py-0.5 text-[10.5px] font-medium transition-colors duration-150', width === size ? 'bg-ink text-white' : 'bg-surface text-ink-soft hover:text-ink')} data-id="element-329" data-mp="371r0ekyl59do">
              {size}%
            </button>)}
        </div>
        <input value={block.caption ?? ''} onFocus={onFocus} data-block-input onChange={event => onUpdate({
        caption: event.target.value
      })} placeholder="Escribe una observación…" className="mt-1 w-full bg-transparent text-[13px] italic text-ink-soft placeholder:text-ink-soft/40 focus:outline-none" data-id="element-330" data-mp="2sde2hifxtggj" />
      </div>
    </div>;
}